import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, AlertCircle, Building2, CreditCard, User, Globe, ChevronDown, Search, Check, MapPin, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import adminService, { AdminBankAccount, AddAdminBankAccountRequest, UpdateAdminBankAccountRequest } from '../../services/admin/adminService';
import DataTable from '../../components/common/DataTable';

const COUNTRIES = [
    { code: 'IN', name: 'India' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'RU', name: 'Russia' },
    { code: 'CN', name: 'China' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SG', name: 'Singapore' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'NP', name: 'Nepal' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'TH', name: 'Thailand' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'PH', name: 'Philippines' },
].sort((a, b) => a.name.localeCompare(b.name));

const AdminBankAccounts: React.FC = () => {
    const [accounts, setAccounts] = useState<AdminBankAccount[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [modal, setModal] = useState<{ show: boolean; mode: 'add' | 'edit'; data: Partial<AdminBankAccount> | null }>({
        show: false,
        mode: 'add',
        data: null
    });
    const [deleteModal, setDeleteModal] = useState<{ show: boolean; account: AdminBankAccount | null }>({
        show: false,
        account: null
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        countries: 0
    });

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async (showToast = false) => {
        try {
            if (showToast) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const data = await adminService.getAllBankAccounts();
            setAccounts(data);

            // Calculate stats
            const activeCount = data.length; // Assuming all returned are active for now, or add logic if status exists
            const uniqueCountries = new Set(data.map(acc => acc.country_code)).size;

            setStats({
                total: data.length,
                active: activeCount,
                countries: uniqueCountries
            });

            if (showToast) {
                toast.success('Bank accounts refreshed!');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to fetch bank accounts');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSaveAccount = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!modal.data) return;

        try {
            setSaving(true);

            // Validate required fields
            if (!modal.data.account_holder_name || !modal.data.bank_name || !modal.data.account_number || !modal.data.country_code) {
                toast.error("All fields are required");
                setSaving(false);
                return;
            }

            if (modal.mode === 'add') {
                const addData: AddAdminBankAccountRequest = {
                    account_holder_name: modal.data.account_holder_name,
                    bank_name: modal.data.bank_name,
                    account_number: modal.data.account_number,
                    country_code: modal.data.country_code,
                };
                await adminService.addBankAccount(addData);
                toast.success('Bank account added successfully!');
            } else {
                if (!modal.data.bank_account_id) return;

                const updateData: UpdateAdminBankAccountRequest = {
                    bank_account_id: modal.data.bank_account_id,
                    account_holder_name: modal.data.account_holder_name,
                    bank_name: modal.data.bank_name,
                    account_number: modal.data.account_number,
                    country_code: modal.data.country_code,
                };
                await adminService.updateBankAccount(updateData);
                toast.success('Bank account updated successfully!');
            }
            setModal({ show: false, mode: 'add', data: null });
            fetchAccounts();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to save bank account');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (!deleteModal.account) return;

        try {
            setDeleting(true);
            await adminService.deleteBankAccount(deleteModal.account.bank_account_id);
            toast.success('Bank account deleted successfully!');
            setDeleteModal({ show: false, account: null });
            fetchAccounts();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete bank account');
        } finally {
            setDeleting(false);
        }
    };

    const openAddModal = () => {
        setModal({
            show: true,
            mode: 'add',
            data: {
                account_holder_name: '',
                bank_name: '',
                account_number: '',
                country_code: ''
            }
        });
    };

    const openEditModal = (account: AdminBankAccount) => {
        setModal({
            show: true,
            mode: 'edit',
            data: { ...account }
        });
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-secondary-900">Admin Bank Accounts</h1>
                    <p className="text-secondary-600 mt-1">Manage bank accounts for manual client payments</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => fetchAccounts(true)}
                        disabled={refreshing}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                    <button
                        onClick={openAddModal}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
                    >
                        <Plus size={18} />
                        Add Bank Account
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 transition-all hover:shadow-md">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <p className="text-secondary-500 text-sm font-medium">Total Accounts</p>
                        <p className="text-2xl font-bold text-secondary-900">{stats.total}</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 transition-all hover:shadow-md">
                    <div className="p-3 bg-green-50 text-green-600 rounded-xl">
                        <CheckCircle2 size={24} />
                    </div>
                    <div>
                        <p className="text-secondary-500 text-sm font-medium">Active</p>
                        <p className="text-2xl font-bold text-secondary-900">{stats.active}</p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 transition-all hover:shadow-md">
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                        <MapPin size={24} />
                    </div>
                    <div>
                        <p className="text-secondary-500 text-sm font-medium">Countries</p>
                        <p className="text-2xl font-bold text-secondary-900">{stats.countries}</p>
                    </div>
                </div>
            </div>

            {/* Accounts Table */}
            <DataTable
                data={accounts}
                loading={loading}
                searchable={true}
                searchPlaceholder="Search by bank name, holder or number..."
                searchFields={['bank_name', 'account_holder_name', 'account_number', 'country_code']}
                defaultPageSize={10}
                emptyMessage="No bank accounts found"
                emptyIcon={<CreditCard size={64} className="text-secondary-300 mx-auto mb-4" />}
                columns={[
                    {
                        header: 'Bank Detail',
                        accessor: (account) => (
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                    <Building2 size={20} className="text-primary-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-secondary-900">
                                        {account.bank_name}
                                    </h3>
                                    <p className="text-xs text-secondary-500">{account.country_code}</p>
                                </div>
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Account Info',
                        accessor: (account) => (
                            <div className="text-sm">
                                <p className="font-medium text-secondary-900">{account.account_number}</p>
                                <p className="text-secondary-500 text-xs">{account.account_holder_name}</p>
                            </div>
                        ),
                    },
                    {
                        header: 'Country',
                        accessor: (account) => (
                            <div className="text-sm flex items-center gap-2">
                                <Globe size={16} className="text-secondary-400" />
                                <span className="font-medium text-secondary-700">{account.country_code}</span>
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Created At',
                        accessor: (account) => (
                            <span className="text-sm text-secondary-600">
                                {new Date(account.created_at).toLocaleDateString()}
                            </span>
                        ),
                        align: 'center',
                        sortable: true,
                    },
                ]}
                renderActions={(account) => (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(account);
                            }}
                            className="bg-primary-50 hover:bg-primary-100 text-primary-600 p-2 rounded-lg transition-colors"
                            title="Edit Bank Account"
                        >
                            <Edit2 size={18} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModal({ show: true, account });
                            }}
                            className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"
                            title="Delete Bank Account"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                )}
            />

            {/* Add/Edit Modal */}
            {modal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full shadow-2xl">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-lg">
                            <h3 className="text-xl font-bold text-secondary-900">
                                {modal.mode === 'add' ? 'Add Bank Account' : 'Edit Bank Account'}
                            </h3>
                            <button
                                onClick={() => setModal({ show: false, mode: 'add', data: null })}
                                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSaveAccount} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-secondary-700">Bank Name</label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-2.5 text-gray-400" size={18} />
                                        <input
                                            required
                                            type="text"
                                            placeholder="e.g., State Bank of India"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                                            value={modal.data?.bank_name || ''}
                                            onChange={(e) => setModal({ ...modal, data: { ...modal.data, bank_name: e.target.value } })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-secondary-700">Account Holder Name</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-2.5 text-gray-400" size={18} />
                                        <input
                                            required
                                            type="text"
                                            placeholder="e.g., John Doe"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                                            value={modal.data?.account_holder_name || ''}
                                            onChange={(e) => setModal({ ...modal, data: { ...modal.data, account_holder_name: e.target.value } })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-secondary-700">Account Number</label>
                                    <div className="relative">
                                        <CreditCard className="absolute left-3 top-2.5 text-gray-400" size={18} />
                                        <input
                                            required
                                            type="text"
                                            placeholder="Account Number"
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                                            value={modal.data?.account_number || ''}
                                            onChange={(e) => setModal({ ...modal, data: { ...modal.data, account_number: e.target.value } })}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-secondary-700">Country</label>
                                    <div className="relative">
                                        <div
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent bg-white cursor-pointer flex items-center justify-between"
                                            onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                                        >
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <Globe className="text-gray-400 flex-shrink-0" size={18} />
                                                <span className={`truncate ${!modal.data?.country_code ? 'text-gray-400' : 'text-gray-900'}`}>
                                                    {modal.data?.country_code
                                                        ? COUNTRIES.find(c => c.code === modal.data?.country_code)?.name || modal.data.country_code
                                                        : 'Select Country'}
                                                </span>
                                            </div>
                                            <ChevronDown size={18} className="text-gray-400" />
                                        </div>

                                        {/* Custom Dropdown */}
                                        {countryDropdownOpen && (
                                            <>
                                                <div
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setCountryDropdownOpen(false)}
                                                />
                                                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 flex flex-col">
                                                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white rounded-t-lg">
                                                        <div className="relative">
                                                            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
                                                            <input
                                                                type="text"
                                                                placeholder="Search country..."
                                                                className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                                                value={countrySearch}
                                                                onChange={(e) => setCountrySearch(e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="overflow-y-auto flex-1 p-1">
                                                        {COUNTRIES.filter(c =>
                                                            c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                            c.code.toLowerCase().includes(countrySearch.toLowerCase())
                                                        ).map((country) => (
                                                            <div
                                                                key={country.code}
                                                                className={`flex items-center justify-between px-3 py-2 text-sm rounded-md cursor-pointer transition-colors ${modal.data?.country_code === country.code
                                                                    ? 'bg-primary-50 text-primary-700 font-medium'
                                                                    : 'text-gray-700 hover:bg-gray-50'
                                                                    }`}
                                                                onClick={() => {
                                                                    setModal({ ...modal, data: { ...modal.data, country_code: country.code } });
                                                                    setCountryDropdownOpen(false);
                                                                    setCountrySearch('');
                                                                }}
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    <span>{country.name}</span>
                                                                    <span className="text-gray-400 text-xs">({country.code})</span>
                                                                </div>
                                                                {modal.data?.country_code === country.code && (
                                                                    <Check size={16} className="text-primary-600" />
                                                                )}
                                                            </div>
                                                        ))}
                                                        {COUNTRIES.filter(c =>
                                                            c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                            c.code.toLowerCase().includes(countrySearch.toLowerCase())
                                                        ).length === 0 && (
                                                                <div className="px-3 py-4 text-center text-gray-500 text-sm">
                                                                    No countries found
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 font-bold"
                                >
                                    {saving ? 'Saving...' : 'Save Bank Account'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setModal({ show: false, mode: 'add', data: null })}
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && deleteModal.account && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full shadow-2xl">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-red-100 rounded-full">
                                    <AlertCircle size={24} className="text-red-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-secondary-900">Delete Bank Account</h3>
                            </div>

                            <p className="text-secondary-600 mb-6">
                                Are you sure you want to delete the account <strong>{deleteModal.account.bank_name} - {deleteModal.account.account_number}</strong>?
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleDeleteAccount}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 font-bold"
                                >
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                                <button
                                    onClick={() => setDeleteModal({ show: false, account: null })}
                                    disabled={deleting}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBankAccounts;
